import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { collectionKeys } from "@/lib/query-keys";
import type {
  CollectionExport,
  PaginatedResponse,
  ReportStatus,
} from "@/types/api";

const IN_PROGRESS: ReportStatus[] = ["PENDING", "PROCESSING"];

export interface CollectionExportsPage {
  /** The newest page of versions, newest first. */
  exports: CollectionExport[];
  /** Every version the collection has, not just this page. */
  count: number;
}

// Module scope so the reference is stable: an inline `select` re-runs on every
// render and hands back a fresh object each time, which would make `data` a new
// identity on every render for no reason.
const selectExports = (
  data: PaginatedResponse<CollectionExport>,
): CollectionExportsPage => ({
  exports: data.results,
  count: data.count,
});

/**
 * A collection's export history — its versions, newest first.
 *
 * Fetched on page load rather than on demand, because the collapsed exports
 * row names how many there are ([[collection-exports-section.spec]]) and a
 * count nobody has fetched cannot be shown. One request on an online-only page
 * buys the row its whole reason to exist.
 *
 * Polls itself on the same 3s cadence as {@link useCollection} while the newest
 * version is still running **and the panel is open**, so an expanded list
 * settles on its own instead of leaving a row stuck at PENDING behind a
 * finished header.
 *
 * `poll` is not an optimisation detail — it is the difference between one and
 * two 3s timers hitting the same collection for the whole of a multi-minute
 * export. The collapsed row reads nothing from this query that moves while an
 * export runs: its date and phase come from the collection's own polled
 * `latest_export`, and the count only changes when an export is created, which
 * {@link useCreateCollectionExport} already invalidates.
 */
export function useCollectionExports(
  projectId: string,
  collectionId: string,
  { poll = false }: { poll?: boolean } = {},
) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useQuery({
    queryKey: collectionKeys.exports(projectId, collectionId),
    queryFn: () =>
      fetchWithAuth<PaginatedResponse<CollectionExport>>(
        `/reports/3.0/projects/${projectId}/collections/${collectionId}/exports/`,
      ),
    select: selectExports,
    refetchInterval: (query) => {
      if (!poll) return false;
      // `query.state.data` is the untransformed payload — `select` runs on the
      // way out to the component, not into the cache.
      const status = query.state.data?.results?.[0]?.status;
      return status && IN_PROGRESS.includes(status) ? 3_000 : false;
    },
    staleTime: 10_000,
  });
}
